import axios from "@/plugins/axios";

const PROVIDERS = {
  google: {
    authUrl: "https://accounts.google.com/o/oauth2/v2/auth",
    scope: "openid email profile",
  },
  microsoft: {
    authUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    scope: "openid email profile",
  },
};

/** Registered OAuth redirect URL (must match Google / Azure app config and the browser callback URL). */
export function getOAuthRedirectUri() {
  return (process.env.VUE_APP_OAUTH_REDIRECT_URI || "").trim();
}

export function isGoogleOAuthConfigured() {
  const uri = getOAuthRedirectUri();
  const id = (process.env.VUE_APP_GOOGLE_CLIENT_ID || "").trim();
  return Boolean(uri && id);
}

export function isMicrosoftOAuthConfigured() {
  const uri = getOAuthRedirectUri();
  const id = (process.env.VUE_APP_MICROSOFT_CLIENT_ID || "").trim();
  return Boolean(uri && id);
}

function getProviderClientId(provider) {
  if (provider === "google") {
    return (process.env.VUE_APP_GOOGLE_CLIENT_ID || "").trim();
  }
  if (provider === "microsoft") {
    return (process.env.VUE_APP_MICROSOFT_CLIENT_ID || "").trim();
  }
  return "";
}

function generateRandomString(length) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(bytes[i] % chars.length);
  }
  return result;
}

async function generateCodeChallenge(codeVerifier) {
  const data = new TextEncoder().encode(codeVerifier);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return btoa(String.fromCodePoint(...hashArray))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}

export async function initializeOAuthLogin(provider, postLoginRedirect = "/") {
  const config = PROVIDERS[provider];
  if (!config) {
    throw new Error(`Unsupported OAuth provider: ${provider}`);
  }
  if (provider === "google" && !isGoogleOAuthConfigured()) {
    throw new Error("Google sign-in is not configured.");
  }
  if (provider === "microsoft" && !isMicrosoftOAuthConfigured()) {
    throw new Error("Microsoft sign-in is not configured.");
  }

  const clientId = getProviderClientId(provider);
  const redirectUri = getOAuthRedirectUri();

  const codeVerifier = generateRandomString(128);
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateRandomString(32);

  sessionStorage.setItem("oauth_code_verifier", codeVerifier);
  sessionStorage.setItem("oauth_state", state);
  sessionStorage.setItem("oauth_provider", provider);
  sessionStorage.setItem("oauth_redirect", postLoginRedirect || "/");

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: config.scope,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    state: state,
    // Show account picker even when the browser already has a Google/Microsoft session.
    prompt: "select_account",
  });

  globalThis.location.href = config.authUrl + "?" + params.toString();
}

export async function exchangeOAuthCode({ code, provider, redirectUri, codeVerifier }) {
  const { data } = await axios.post("/auth/oauth/" + provider, {
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });
  return data;
}
